import * as React from 'react'
import { useState } from 'react'
import { Button, Divider, Modal, Radio, Space, Steps, Tooltip } from 'antd'
// eslint-disable-next-line no-unused-vars
import { RadioChangeEvent } from 'antd/lib/radio'
import styles from './styles.module.scss'
import { BarDropdown } from '../bar-dropdown/BarDropdown'
import { ContactUsIcon } from '../../icons/ContactUsIcon'
import { Routes } from '../../routing/routes'

const { Step } = Steps

const SUPPORT_EMAIL = 'VantageSupport@thermofisher.com'
const DATA_RELATED_SUBJECT = 'Data Related'

const SUBJECT_OPTIONS = [
  'Account Access/ Login',
  'General question/ Other',
  DATA_RELATED_SUBJECT
]

const STEP_KEYS = {
  HELP: 'help',
  SUBJECT: 'subject',
  QUERY: 'query',
  COHORT: 'cohort',
  EMAIL: 'email'
}

type IconProps = {
  onClick: () => void
}

const Icon = ({ onClick }: IconProps) => {
  return (
    <Tooltip
      align={{ offset: [-10] }}
      overlayClassName='nav-bar-app'
      placement='bottom'
      title='Contact Us'
    >
      <ContactUsIcon className={styles.contactUsIcon} onClick={onClick} />
    </Tooltip>
  )
}

const getMailToHref = (subject: string) => {
  const body = [
    'Please provide as many details about your question/request as possible.',
    '',
    'If you are contacting us about an issue with a query, please provide the query ID where available.',
    '',
    'If relevant, please share the query/project with Test Account before sending.'
  ].join('\n')

  return `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
    subject
  )}&body=${encodeURIComponent(body)}`
}

export const ContactUs = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [currentStep, setCurrentStep] = useState(0)
  const [subject, setSubject] = useState(SUBJECT_OPTIONS[0])

  const isDataRelated = subject === DATA_RELATED_SUBJECT

  const steps = isDataRelated
    ? [
        { key: STEP_KEYS.HELP, title: 'Help' },
        { key: STEP_KEYS.SUBJECT, title: 'Subject' },
        { key: STEP_KEYS.QUERY, title: 'Query' },
        { key: STEP_KEYS.COHORT, title: 'Cohort' },
        { key: STEP_KEYS.EMAIL, title: 'Email support' }
      ]
    : [
        { key: STEP_KEYS.HELP, title: 'Help' },
        { key: STEP_KEYS.SUBJECT, title: 'Subject' },
        { key: STEP_KEYS.EMAIL, title: 'Email support' }
      ]

  const currentStepKey = steps[currentStep].key
  const isFirstStep = currentStep === 0
  const isLastStep = currentStep === steps.length - 1

  const closeModal = () => {
    setIsOpen(false)
    setCurrentStep(0)
  }

  const handleSubjectChange = (event: RadioChangeEvent) => {
    setSubject(event.target.value)
    setCurrentStep(1)
  }

  const goBack = () => {
    if (isFirstStep) {
      closeModal()
      return
    }

    setCurrentStep(currentStep - 1)
  }

  const goNext = () => {
    if (!isLastStep) {
      setCurrentStep(currentStep + 1)
    }
  }

  return (
    <div className={styles.contactUsWrapper}>
      <BarDropdown
        dropDownClassName={styles.dropDown}
        icon={<Icon onClick={() => setIsOpen(true)} />}
        dataTestId='contact-us-dropdown'
      >
        <div />
      </BarDropdown>

      <Modal
        title={<span className={styles.modalTitle}>Contact Us</span>}
        open={isOpen}
        onCancel={closeModal}
        footer={null}
        width={720}
        className={styles.contactUsModal}
      >
        <div className={styles.modalContent}>
          <Steps current={currentStep} className={styles.contactUsSteps}>
            {steps.map((step) => (
              <Step key={step.key} title={step.title} />
            ))}
          </Steps>

          <Divider />

          <div className={styles.contactUsStepViewport}>
            <div key={currentStepKey} className={styles.contactUsStepPanel}>
              {currentStepKey === STEP_KEYS.HELP && (
                <div>
                  <p>
                    Contact our Vantage Support Desk with any questions related
                    to functionality, data, or if you would like our team to
                    complete an analysis on your behalf.
                  </p>

                  <p>
                    Before submitting your question, you may find a quick answer
                    in:
                  </p>

                  <ul>
                    <li>
                      <a href={Routes.Glossary}>Glossary</a>
                    </li>
                    <li>
                      <a href={Routes.FaqPDF} download>
                        FAQs
                      </a>
                    </li>
                    <li>
                      <a href={Routes.Training}>Training page</a>
                    </li>
                  </ul>
                </div>
              )}

              {currentStepKey === STEP_KEYS.SUBJECT && (
                <div>
                  <p>
                    Please use one of the following in the subject line of your
                    email to best describe your request:
                  </p>

                  <Radio.Group value={subject} onChange={handleSubjectChange}>
                    <Space direction='vertical'>
                      {SUBJECT_OPTIONS.map((option) => (
                        <Radio key={option} value={option}>
                          {option}
                        </Radio>
                      ))}
                    </Space>
                  </Radio.Group>
                </div>
              )}

              {currentStepKey === STEP_KEYS.QUERY && (
                <div>
                  <p>
                    If you are contacting us about an issue with a query, please
                    provide the query ID, where available, or share the query
                    with Test Account so we are able to look into it.
                  </p>

                  <Divider />

                  <h4>Share Query Builder / Cohort Analyzer query</h4>

                  <ol>
                    <li>Go to ‘My Saved Queries’.</li>
                    <li>
                      Locate the query and click on the ‘Not Shared/Shared’
                      button to share it.
                    </li>
                  </ol>

                  <img
                    src={`${process.env.PUBLIC_URL}/query-builder-share.png`}
                    alt='Share query with Test Account'
                    className={`${styles.contactUsImage} ${styles.contactUsImageSmall}`}
                  />
                </div>
              )}

              {currentStepKey === STEP_KEYS.COHORT && (
                <div>
                  <p>
                    If your request relates to a Cohort Builder project, share
                    the project with Test Account before contacting support.
                  </p>

                  <Divider />

                  <h4>Share Cohort Builder project</h4>

                  <ol>
                    <li>Locate your project in ‘My Projects’ tab.</li>
                    <li>Click on ‘Share this project’.</li>
                    <li>Find ‘Test Account’ and click ‘Share’.</li>
                  </ol>

                  <img
                    src={`${process.env.PUBLIC_URL}/cohort-builder-share.png`}
                    alt='Share project with Test Account'
                    className={`${styles.contactUsImage} ${styles.contactUsImageSmall}`}
                  />
                </div>
              )}

              {currentStepKey === STEP_KEYS.EMAIL && (
                <div>
                  <p>
                    Please provide us with as many details about your
                    question/request as you can and we will be in touch soon.
                    Your email will be opened in your email application and
                    addressed to:
                  </p>
                  <p>
                    <strong>{SUPPORT_EMAIL}</strong>
                  </p>

                  <Button
                    href={getMailToHref(subject)}
                    className={`${styles.contactUsButton} ${styles.emailSupportButton}`}
                  >
                    Email Vantage Support
                  </Button>
                </div>
              )}
            </div>
          </div>

          <Divider />

          <Space>
            <Button
              htmlType='button'
              onClick={goBack}
              className={styles.contactUsButton}
            >
              {isFirstStep ? 'Cancel' : 'Back'}
            </Button>

            {!isLastStep && (
              <Button onClick={goNext} className={styles.contactUsButton}>
                Next
              </Button>
            )}
          </Space>
        </div>
      </Modal>
    </div>
  )
}
